import {
  getSpeakerColorToken,
  type SpeakerColor,
} from "@karda/design-tokens";

export type { SpeakerColor };

/**
 * Display name for a speaker. Bare numeric labels (from raw diarization, e.g.
 * "0", "2") get a "Speaker N" prefix; anything the user has named (e.g.
 * "Officer Reyes") is shown as-is.
 */
export const formatSpeakerLabel = (
  speaker: string | number | undefined,
): string => {
  const label = String(speaker ?? "").trim();
  if (!label) return "Unknown speaker";
  return /^\d+$/.test(label) ? `Speaker ${label}` : label;
};

/**
 * Mode-aware speaker identity color (validated token pairs — chips must
 * render soft/text/border, marks render primary; see
 * design-tokens/color/speakers.ts). Pass `theme.palette.mode`; video-overlay
 * consumers sit on a dark scrim regardless of app theme and should pass
 * "dark" explicitly.
 */
export const getSpeakerColor = (
  speaker: string | number | undefined,
  mode: "light" | "dark",
): SpeakerColor => {
  const speakerIndex =
    typeof speaker === "number"
      ? speaker
      : typeof speaker === "string"
        ? parseInt(speaker, 10) || 0
        : 0;
  return getSpeakerColorToken(speakerIndex, mode);
};

/**
 * Color for a speaker by its position in the known speaker list, so arbitrary
 * string labels (e.g. "Officer Reyes") still get stable, distinct colors.
 * Falls back to the numeric/legacy scheme when the label isn't in the list.
 */
export const getSpeakerColorByLabel = (
  label: string | number | undefined,
  speakers: string[],
  mode: "light" | "dark",
): SpeakerColor => {
  const idx = speakers.indexOf(String(label ?? ""));
  if (idx >= 0) return getSpeakerColorToken(idx, mode);
  return getSpeakerColor(label, mode);
};
